import * as Rest from "../Generated/RestClient";

export enum BotTypes {
    None,
    SDK,
    ABSv1,
    ABSv2,
    ABSCode,
    ABSGACreate
}

export function getDeploymentState(bot: Rest._BotService = {}): string {
    const deployment = bot.ProvisionInfo ? bot.ProvisionInfo.Deployment : {};
    return deployment && deployment.properties ? deployment.properties.provisioningState : null;
}

export const RollbackEndpointVersionName = 'rollback_to';

/**
 * V2 bot utils to get a bot type - this relies on the bot being a newer bot created through Azure (Ibiza).
 * Note the bot kinds here match the values in Ibiza and NOT the values in BotUtils.
 * todo: refactor/delete BotUtils.
 */
export class BotUtilsV2 {
    /** Is this a registration or Connectivity only bot? */
    public static isRegistrationBot(bot: Rest.BotService): boolean {
        return BotUtilsV2._isBotKind(bot, "bot");
    }

    /** Is this an Azure Functions bot? */
    public static isFunctionsBot(bot: Rest.BotService): boolean {
        return BotUtilsV2._isBotKind(bot, "function");
    }

    /** Is this an SDK bot? */
    public static isSdkBot(bot: Rest.BotService): boolean {
        return BotUtilsV2._isBotKind(bot, "sdk");
    }

    /** Is this a Conversation Designer bot? */
    public static isConversationDesignerBot(bot: Rest.BotService): boolean {
        return BotUtilsV2._isBotKind(bot, "designer");
    }

    /** Has this bot been migrated to be managed in the Azure Ibiza portal? */
    public static isMigratedBot(bot: Rest.BotService): boolean {
        if (!bot) {
            throw new Error("bot must be defined");
        }

        return bot.Kind && bot.Endpoints && bot.Endpoints.some(ep => ep.Version === RollbackEndpointVersionName);
    }

    /** will we need to show migration UI to the user? */
    public static needsMigration(bot: Rest.BotService) : boolean {
        if (!bot) {
            throw new Error("bot must be defined");
        }
        return !bot.Kind && bot.BotServiceRuntimeVersion !== "ABSv2";
    }

    /** Has this bot been migrated to be managed in the Azure Ibiza portal? */
    public static isMigratedBotDocument(bot: Rest.BotDocument): boolean {
        if (!bot) {
            throw new Error("bot must be defined");
        }

        // Azure bots will have a kind and migrated bots will have endpoints.
        if (!bot.Kind || !bot.Endpoints) {
            return false;
        }

        if (bot.Endpoints) {
            try {
                const endpoints = JSON.parse(bot.Endpoints);
                return Array.isArray(endpoints) && endpoints.some(x => {
                    return (x.Version || x.version) === RollbackEndpointVersionName;
                });
            }
            catch (e) {
                // Ingore - this isn't a migrated bot.
            }
        }
        return false;
    }

    /** Is this a bot managed through the Azure Ibiza portal? */
    public static isAzureIbizaBot(bot: Rest.BotService | Rest.BotDocument): boolean {
        if (!bot) {
            throw new Error("bot must be defined");
        }

        return !!bot.Kind;
    }

    /** Channels that are allowed in migration optional bots. */
    public static migrationOptionalChannels = ["cortana", "skype", "msteams", "webchat", "outlook", "m365extensions"];

    /**
     * Is migration optional for this bot, ie, is this a free bot, meaning a non-Ibiza bot that is
     * configured for Cortana, Skype, and/or MSTeams, with no other channel registered except
     * webchat. Free bots have a relaxed migration deadline, and don't promote migration aggressively.
     */
    public static isMigrationOptional(bot: Rest.BotService | Rest.BotDocument): boolean {
        if (this.isAzureIbizaBot(bot)) {
            return false;
        }
        if (!bot.ConfiguredChannels) {
            return false;
        }
        // bingbizbot: xinan - Bing Local bots - they'll migrate once Management API is available
        // skypeOA: imriczi,timale - Skype Professional Account bots - they'll migrate once Management API is available
        // cernerbots: jeffphil,molybos - just need more time for internal issues with Azure approval
        if (bot.SystemHashtags.indexOf("bingbizbot") >= 0
            || bot.SystemHashtags.indexOf("skypeOA") >= 0
            || bot.SystemHashtags.indexOf("cernerbots") >= 0
            || bot.SystemHashtags.indexOf("migrationOptional") >= 0)
        {
            // these bots have been specifically marked as not needing migration
            return true;
        }
        // absLegacy bots are required to migrate unless hashtagged otherwise
        if (bot.SystemHashtags.indexOf("absLegacy") >= 0) {
            return false;
        }
        
        let hasAtLeastOne = false;
        for (let i = 0; i < bot.ConfiguredChannels.length; i++) {
            const channel = bot.ConfiguredChannels[i].toLowerCase();
            if (this.migrationOptionalChannels.indexOf(channel) < 0) {
                return false;
            } else if (channel !== "webchat") {
                // need at least one allowed channel that isn't webchat
                hasAtLeastOne = true;
            }
        }
        return hasAtLeastOne;
    }

    private static _isBotKind(bot: Rest.BotService, kind: string): boolean {
        if (!bot) {
            throw new Error("bot must be defined");
        }
        return bot.Kind === kind;
    }
}

// We can't be doing this kind of object probing. This needs to be a value
// provided on the BotService.
export class BotUtils {
    public static isABSv1Bot(bot: Rest.BotService): boolean {
        return !!(bot && bot.ProvisionInfo && !bot.ProvisionInfo.BotRunTimeVersion);
    }

    public static isABSv2Bot(bot: Rest.BotService): boolean {
        return !!(bot && bot.ProvisionInfo && bot.ProvisionInfo.BotRunTimeVersion);
    }

    public static isABSBot(bot: Rest.BotService): boolean {
        return !!(bot && bot.ProvisionInfo);
    }

    public static isSDKBot(bot: Rest.BotService): boolean {
        return !!(bot && !bot.ProvisionInfo);
    }

    public static isABSCodeBot(bot: Rest.BotService) : boolean {
        return !!(bot && bot.ProvisionInfo && bot.ProvisionInfo.HostingEnvironment === Rest.HostingEnvironment.AzureWebApp);
    }

    public static getBotType(bot: Rest.BotService): BotTypes {
        if (this.isSDKBot(bot))
            return BotTypes.SDK;
        if (this.isABSCodeBot(bot))
            return BotTypes.ABSCode;
        if (this.isABSv1Bot(bot))
            return BotTypes.ABSv1;
        if (this.isABSv2Bot(bot))
            return BotTypes.ABSv2;
        return BotTypes.None;
    }

    public static isDeploymentFinished(bot: Rest._BotService): boolean {
        const state = getDeploymentState(bot);
        const deployment = bot.ProvisionInfo ? bot.ProvisionInfo.Deployment : {};
        const pending = !bot.Endpoint && deployment && deployment.properties ? deployment.properties.intercom_pending_function_url : false;
        return !pending && (state == "Succeeded" || state == "Failed");
    }
}



// WEBPACK FOOTER //
// ./Client/Utils/BotUtils.ts